import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, type Chat, type Workspace, type Plugin } from '../api/client'
import { capsFor, effectiveView } from '../lib/viewMode'
import { copyText } from '../lib/clipboard'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuth } from '../hooks/useAuth'
import { WhimerV3View } from '../components/WhimerV3View'
import { NewChatModal } from '../components/NewChatModal'
import { PanelHost } from '../components/PanelHost'
import { useIsNarrow } from '../hooks/useIsNarrow'
import { Icon } from '../components/Icon'
import { RelativeTime } from '../components/RelativeTime'
import { agentTag } from '../lib/agentTag'
import { syncExternalSlots } from '../slots/registerExternal'
import { allSlotContributions } from '../slots'
import { subscribeDocument, getCurrentDocument, docKey } from '../lib/documentViewer'

// ─── Helpers ───────────────────────────────────────────────────────────────────

// Render-mode decision logic lives in ../lib/viewMode (pure, unit-tested).

// ─── Component ─────────────────────────────────────────────────────────────────
//
// Open WebUI-style app shell: a persistent left sidebar (Recent / Workspaces) +
// a chat main panel. The route param `chatId` drives which chat is open; the
// owning workspace is derived from the loaded chat. All chat / WS / resume logic
// is preserved from the original ChatPage — only layout + entry flow changed.

export function AppShell() {
  const { chatId }        = useParams<{ chatId: string }>()
  const navigate          = useNavigate()
  const { token, logout } = useAuth()
  const wsHook            = useWebSocket(token)

  // Server rejected the token (expired / invalid). The socket no longer retries it
  // (see useWebSocket onclose) — clear the dead token and bounce to /login so the user
  // can re-authenticate, instead of sitting on a silently-broken screen.
  useEffect(() => {
    if (wsHook.authState === 'failed') {
      logout()
      navigate('/login', { replace: true })
    }
  }, [wsHook.authState, logout, navigate])

  const [chats, setChats]                 = useState<Chat[]>([])
  const [workspaces, setWorkspaces]       = useState<Workspace[]>([])
  const [currentChat, setCurrentChat]     = useState<Chat | null>(null)
  const [plugins, setPlugins]             = useState<Plugin[]>([])
  const [showSettings, setShowSettings]   = useState(false)
  // Panel host (PLUGIN_MODULES.md → panel contribution). `activePanel` = which panel is open
  // (last opened wins, null = none). Desktop shows it as a side column; on mobile the panel is a
  // full-screen swap and `mobilePanelView` tracks whether the panel (true) or the chat (false)
  // is on screen. Ephemeral (resets on reload), like the old showVoice it replaces.
  // Remembered GLOBALLY (one value, all chats) + persisted so it survives a refresh — open a
  // different panel from another chat if you want; we deliberately don't store per-chat (3a).
  const [activePanel, setActivePanel]       = useState<string | null>(
    () => { try { return localStorage.getItem('whimer.activePanel') } catch { return null } },
  )
  const [mobilePanelView, setMobilePanelView] = useState(false)
  const [voiceEnabled, setVoiceEnabled]     = useState(true)
  // Whether any plugin contributes the `sidebar` embed (the excalidraw canvas). The slot registry is
  // a module-level array (not React state), so we snapshot this after the slots fetch — it gates the
  // header pencil button that opens the canvas panel. The canvas itself renders via the panel host.
  const [hasSidebarSlots, setHasSidebarSlots] = useState(false)
  // Desktop panel width (the split side column) — drag the handle on its left edge to resize. Shared
  // by every panel (voice/document/canvas); persisted so it survives a refresh. Mobile ignores it
  // (the panel is a full-screen swap there).
  const [panelWidth, setPanelWidth] = useState<number>(
    () => { try { const n = Number(localStorage.getItem('whimer.panelWidth')); return n >= 320 ? n : 480 } catch { return 480 } },
  )
  const narrow = useIsNarrow()
  const [showNewChat, setShowNewChat]     = useState(false)
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed]   = useState(false)
  const [sessionFlash, setSessionFlash]   = useState(false)

  // Sidebar search + per-row "..." menu
  const [chatSearch, setChatSearch]       = useState('')
  const [originFilter, setOriginFilter]   = useState<'all' | 'user' | 'api'>('all')
  // Per-chat API token modal (spec/CHAT_API.md §5).
  const [tokenModal, setTokenModal]       = useState<{ chatId: string; token: string | null } | null>(null)
  const [renameModal, setRenameModal]     = useState<{ chatId: string; name: string } | null>(null)
  const [tokenBusy, setTokenBusy]         = useState(false)
  const [menuOpenId, setMenuOpenId]       = useState<string | null>(null)
  const [bulkMenuOpen, setBulkMenuOpen]   = useState(false)   // bulk-delete menu (next to search)
  const [menuPos, setMenuPos]             = useState<{ top: number; right: number }>({ top: 0, right: 0 })

  // Chat settings panel state
  const [editName, setEditName]           = useState('')
  const [editPlugin, setEditPlugin]       = useState('')
  const [editCommand, setEditCommand]     = useState('')
  const [copiedTmux, setCopiedTmux]       = useState(false)

  // Resuming state (auto-resume on chat selection)
  const [isResuming, setIsResuming]       = useState(false)
  // Force-restart state (manual ↻ button)
  // Bumped on focus/visibility regain → children re-fetch history / re-seed
  // (Problem 2: switching to `--resume` in a terminal advances the session; the
  // web should pick it up when you come back without a manual browser refresh).
  const [refreshNonce, setRefreshNonce]   = useState(0)

  // Workspace that "New Chat" will create into. Defaults to the open chat's
  // workspace, else the user's General workspace (resolved lazily).
  const generalWsRef = useRef<string | null>(null)
  // Last broadcast state per chat → only re-sync the screen on a real transition
  // INTO active. attachConnection re-sends 'active' on EVERY attach, so bumping
  // refreshNonce on every 'active' creates active→refresh→re-attach→active… = the
  // terminal resets continuously (the "จอสั่น" flicker). Guard on the transition.
  const prevChatStateRef = useRef<Record<string, string>>({})

  // Stream-activity → debounced refetch of the authoritative list.
  // last_activity is now DERIVED server-side from real stored messages (last 'submit' /
  // native_commit), so the client must NOT fake a timestamp: writing Date.now() on every
  // stream event is exactly what made a chat jump to "just now" just by being opened (the
  // open triggers a repaint → onActivity, but no message was sent). Instead we debounce a
  // pull of the server truth — an open returns the SAME order (nothing was written), while a
  // real send / finalized turn moves the chat because the server's derived value changed.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bumpChatActivity = useCallback((_cId: string) => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current)
    reloadTimer.current = setTimeout(() => {
      api.chats.listAll().then(setChats).catch(console.error)
    }, 800)
  }, [])

  // Switch a chat's render view (clean ⇄ terminal). Optimistic local update +
  // persist so reopening the chat remembers. Same live PTY — only the view swaps.
  const setViewMode = useCallback(async (view: 'clean3' | 'term3') => {
    const id = currentChat?.id
    if (!id) return
    setCurrentChat(c => (c && c.id === id ? { ...c, view_mode: view } : c))
    setChats(prev => prev.map(c => (c.id === id ? { ...c, view_mode: view } : c)))
    try { await api.chats.update(id, { view_mode: view }) }
    catch (e) { console.error('[view] update failed:', e) }
  }, [currentChat])

  // Open / toggle a panel (panel host). Desktop: shows it as the side column. Mobile: swaps the
  // full-screen view to the panel. Pressing the open button for the already-active panel brings
  // it to front if hidden on mobile, otherwise closes it. See PLUGIN_MODULES.md.
  const openPanel = useCallback((id: string) => {
    if (activePanel === id) {
      if (narrow && !mobilePanelView) { setMobilePanelView(true); return } // hidden → bring to front
      setActivePanel(null); setMobilePanelView(false); return              // already shown → close
    }
    setActivePanel(id)
    setMobilePanelView(true)
  }, [activePanel, narrow, mobilePanelView])

  const closePanel = useCallback(() => {
    setActivePanel(null)
    setMobilePanelView(false)
  }, [])

  // Persist the panel width across refreshes.
  useEffect(() => {
    try { localStorage.setItem('whimer.panelWidth', String(Math.round(panelWidth))) } catch { /* in-memory only */ }
  }, [panelWidth])

  // Drag the panel's left edge to resize (desktop only). The panel is docked right, so width grows
  // as the pointer moves left: width = viewport - clientX. Clamped to a sane min + 70% of the screen.
  // A full-window transparent overlay is mounted for the duration of the drag: without it, the moment
  // the pointer crosses over the panel's iframe (the canvas), the iframe's document swallows the
  // pointermove events and the parent stops tracking — so you could widen (pointer moves toward the
  // chat) but never shrink (pointer moves onto the iframe). The overlay keeps every move in this doc.
  const startPanelResize = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    const overlay = document.createElement('div')
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;cursor:col-resize'
    document.body.appendChild(overlay)
    const onMove = (ev: PointerEvent) => {
      setPanelWidth(Math.max(320, Math.min(window.innerWidth * 0.7, window.innerWidth - ev.clientX)))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      overlay.remove()
      document.body.style.userSelect = ''
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  // Persist the remembered panel (global, survives refresh). null → clear the key. The
  // 'document' panel is EXCLUDED: its content lives in an in-memory store that resets on
  // reload, so re-opening it on refresh would just show an empty reader.
  useEffect(() => {
    try {
      if (activePanel && activePanel !== 'document') localStorage.setItem('whimer.activePanel', activePanel)
      else localStorage.removeItem('whimer.activePanel')
    } catch { /* localStorage unavailable — in-memory only */ }
  }, [activePanel])

  // Open-document → bring the document panel on-screen. A chat attachment card calls
  // openDocument() (deep inside renderContent / a tool turn, no prop chain to here); we
  // track that store and surface the panel. Fires on each NEW doc (identity), so tapping
  // another card after closing the panel reopens it. See lib/documentViewer + docAttach.
  const openDoc = useSyncExternalStore(subscribeDocument, getCurrentDocument)
  useEffect(() => {
    if (!openDoc) return
    setActivePanel('document')
    setMobilePanelView(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDoc && docKey(openDoc)])

  // Force-restart the live session (manual ↻). Kills the PTY + tmux session and
  // respawns fresh via --resume — recovers a stuck/hung session that a refresh
  // can't (re-attach only respawns when there's NO active PTY). Backend broadcasts
  // chat_state, so the dot/badge update on their own.
  // Problem 2: when the tab/app regains focus or becomes visible, bump the nonce
  // so the open chat re-fetches history (clean view) / re-seeds the screen
  // (terminal view). Throttled to avoid hammering on rapid focus flaps.
  useEffect(() => {
    let last = 0
    const trigger = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - last < 1500) return
      last = now
      setRefreshNonce(n => n + 1)
    }
    document.addEventListener('visibilitychange', trigger)
    window.addEventListener('focus', trigger)
    return () => {
      document.removeEventListener('visibilitychange', trigger)
      window.removeEventListener('focus', trigger)
    }
  }, [])

  // ─── Load all chats + workspaces + plugins ─────────────────────────────────

  const reloadChats = useCallback(() => {
    api.chats.listAll().then(setChats).catch(console.error)
  }, [])

  useEffect(() => {
    reloadChats()
    api.workspaces.list().then(setWorkspaces).catch(console.error)
    api.plugins.list().then(setPlugins).catch(console.error)
    // C3 — declarative UI-widget contributions → slot registry (SlotHosts render them). Fail-open:
    // on error the registry just stays without external widgets (no crash, internal slots intact).
    api.slots.list()
      .then((dtos) => { syncExternalSlots(dtos); setHasSidebarSlots(allSlotContributions().some((c) => c.slot === 'sidebar')) })
      .catch(console.error)
    // Voice is a toggleable internal feature (PLUGIN_MODULES.md Phase 2) — hide its button +
    // panel when off. Fail-open: on error keep it on (default true).
    api.settings.featurePlugins.list()
      .then(fs => {
        setVoiceEnabled(fs.find(f => f.id === 'voice')?.enabled ?? true)
      })
      .catch(() => { setVoiceEnabled(true) })
  }, [reloadChats])

  // ─── Auto-open most recent chat at "/" ──────────────────────────────────────

  useEffect(() => {
    if (chatId) return
    if (chats.length === 0) return
    const mostRecent = [...chats].sort(
      (a, b) => (b.last_activity ?? 0) - (a.last_activity ?? 0),
    )[0]
    if (mostRecent) navigate(`/c/${mostRecent.id}`, { replace: true })
  }, [chatId, chats, navigate])

  // ─── Load current chat + auto-resume ──────────────────────────────────────

  useEffect(() => {
    if (!chatId) {
      setCurrentChat(null)
      return
    }
    setIsResuming(false) // reset on chat switch

    api.chats.get(chatId)
      .then(async chat => {
        setCurrentChat(chat)
        setEditName(chat.name)
        setEditPlugin(chat.agent_plugin || '')
        setEditCommand(chat.command)

        // Auto-resume sleeping PTY/API chats on selection.
        if (chat.state === 'sleeping' && !chat.hasActivePty && capsFor(chat, plugins).cleanView) {
          setIsResuming(true)
          try {
            await api.chats.resume(chatId)
            const updated = await api.chats.get(chatId)
            setCurrentChat(updated)
            setChats(prev => prev.map(c => c.id === chatId ? { ...c, ...updated } : c))
          } catch (err) {
            console.error('[auto-resume] failed:', err)
          } finally {
            setIsResuming(false)
          }
        }
      })
      .catch(console.error)
    // Close mobile sidebar when navigating to a chat
    setShowMobileSidebar(false)
  }, [chatId])

  // ─── WS: chat state changes ───────────────────────────────────────────────

  useEffect(() => {
    wsHook.onChatState((cId, state) => {
      const prevState = prevChatStateRef.current[cId]
      prevChatStateRef.current[cId] = state
      if (cId === chatId) {
        setCurrentChat(prev => prev ? { ...prev, state } : prev)
        // Re-sync the chat pane only on a real transition INTO active (a restart /
        // auto-restart / resume just finished). NOT on every 'active' message —
        // attachConnection re-sends 'active' on each attach, so bumping every time
        // would loop (active → refresh → re-attach → active …) and the terminal
        // would reset continuously (the flicker).
        if (state === 'active' && prevState && prevState !== 'active') {
          setRefreshNonce(n => n + 1)
        }
      }
      setChats(prev => prev.map(c => c.id === cId ? { ...c, state } : c))
    })
  }, [wsHook, chatId])

  // ─── WS: session ID auto-captured ─────────────────────────────────────────

  useEffect(() => {
    wsHook.onSessionIdCaptured((cId, sessionId, agentPlugin) => {
      setChats(prev => prev.map(c =>
        c.id === cId
          ? { ...c, agent_session_id: sessionId, agent_plugin: agentPlugin || c.agent_plugin }
          : c
      ))
      if (cId === chatId) {
        setCurrentChat(prev => prev
          ? { ...prev, agent_session_id: sessionId, agent_plugin: agentPlugin || prev.agent_plugin }
          : prev
        )
        if (agentPlugin) setEditPlugin(agentPlugin)
        setSessionFlash(true)
        setTimeout(() => setSessionFlash(false), 3000)
      }
    })

    wsHook.onChatNameUpdated((cId, name) => {
      setChats(prev => prev.map(c => c.id === cId ? { ...c, name } : c))
      if (cId === chatId) {
        setCurrentChat(prev => prev ? { ...prev, name } : prev)
        setEditName(name)
      }
    })
  }, [wsHook, chatId])

  // ─── Resolve the workspace a new chat should be created into ────────────────

  const resolveTargetWorkspace = useCallback(async (): Promise<string> => {
    // Prefer the workspace of the chat currently open.
    if (currentChat?.workspace_id) return currentChat.workspace_id
    // Else use (resolve-or-create) the user's General workspace.
    if (generalWsRef.current) return generalWsRef.current
    const ws = await api.chats.generalWorkspace()
    generalWsRef.current = ws.id
    // Make sure it's in the workspace list for grouping.
    setWorkspaces(prev => prev.some(w => w.id === ws.id) ? prev : [ws, ...prev])
    return ws.id
  }, [currentChat])

  // ─── Create chat (from NewChatModal) ──────────────────────────────────────

  const handleCreateChat = useCallback(async (config: {
    name?: string
    agentPlugin?: string
    agentSessionId?: string
    connectionType: 'pty' | 'cli'
    spawnMode?: 'tmux' | 'native-pty'
    model_id?: string
  }) => {
    const workspaceId = await resolveTargetWorkspace()
    const chat = await api.chats.create(workspaceId, {
      name:            config.name,
      agentPlugin:     config.agentPlugin,
      agentSessionId:  config.agentSessionId,
      connection_type: config.connectionType,
      spawnMode:       config.spawnMode,
      model_id:        config.model_id,
    })
    if (!chat.resumed) {
      setChats(prev => [{ ...chat, last_activity: Date.now() }, ...prev])
    }
    navigate(`/c/${chat.id}`)
  }, [resolveTargetWorkspace, navigate])

  const handleOpenExistingChat = useCallback((id: string) => {
    navigate(`/c/${id}`)
  }, [navigate])

  // ─── Clean all (kill PTYs, keep history) ─────────────────────────────────

  const cleanAll = async () => {
    try {
      const result = await api.chats.cleanAll()
      setChats(prev => prev.map(c =>
        result.cleaned.includes(c.id) ? { ...c, state: 'sleeping', hasActivePty: false } : c
      ))
    } catch (err) {
      console.error('Failed to clean chats:', err)
    }
  }

  // ─── Delete chat ──────────────────────────────────────────────────────────

  const deleteChat = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpenId(null)
    if (!confirm('Delete this chat?')) return
    try {
      await api.chats.delete(id)
      setChats(prev => prev.filter(c => c.id !== id))
      if (chatId === id) navigate('/')
    } catch (err) {
      console.error('Failed to delete chat:', err)
    }
  }

  // Bulk delete from the search bar: all / only pinned / only unpinned. Optimistic + resync.
  const bulkDelete = async (scope: 'all' | 'pinned' | 'unpinned') => {
    setBulkMenuOpen(false)
    const targets = chats.filter(c =>
      scope === 'all' ? true : scope === 'pinned' ? !!c.pinned : !c.pinned)
    if (targets.length === 0) return
    const label = scope === 'all' ? 'ทั้งหมด' : scope === 'pinned' ? 'ที่ Pin ไว้' : 'ที่ไม่ได้ Pin'
    if (!confirm(`ลบแชท${label} ${targets.length} รายการ? (กู้คืนไม่ได้)`)) return
    const ids = new Set(targets.map(c => c.id))
    setChats(prev => prev.filter(c => !ids.has(c.id)))   // optimistic
    if (chatId && ids.has(chatId)) navigate('/')          // left the open chat
    await Promise.allSettled(targets.map(c => api.chats.delete(c.id)))
    reloadChats()                                          // resync with server truth
  }

  // Pin / unpin a chat — pinned chats stick to the top of the list. Optimistic.
  const togglePin = async (chat: Chat, e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpenId(null)
    const next = !chat.pinned
    setChats(prev => prev.map(c => c.id === chat.id ? { ...c, pinned: next ? 1 : 0 } : c))
    try { await api.chats.update(chat.id, { pinned: next ? 1 : 0 }) }
    catch (err) {
      console.error('Failed to pin chat:', err)
      setChats(prev => prev.map(c => c.id === chat.id ? { ...c, pinned: next ? 0 : 1 } : c))
    }
  }

  // Issue / rotate a chat's per-chat API token (spec/CHAT_API.md §5), then show it in the modal.
  const genChatToken = async (chatId: string) => {
    setTokenBusy(true)
    try {
      const r = await api.chats.genApiToken(chatId)
      setTokenModal({ chatId, token: r.token })
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, api_token: r.token } : c))
    } catch (err) {
      console.error('Failed to generate chat token:', err)
    } finally {
      setTokenBusy(false)
    }
  }

  // Open the per-row "..." menu, anchored (fixed) to the button so it escapes the
  // sidebar's overflow clipping.
  const openRowMenu = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (menuOpenId === id) { setMenuOpenId(null); return }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
    setMenuOpenId(id)
  }

  // Close the row menu on any outside click / resize.
  useEffect(() => {
    if (!menuOpenId) return
    const close = () => setMenuOpenId(null)
    document.addEventListener('click', close)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('click', close)
      window.removeEventListener('resize', close)
    }
  }, [menuOpenId])

  // ─── Resume chat (manual fallback) ───────────────────────────────────────

  const resumeChat = async () => {
    if (!chatId || isResuming) return
    setIsResuming(true)
    try {
      await api.chats.resume(chatId)
      const chat = await api.chats.get(chatId)
      setCurrentChat(chat)
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, ...chat } : c))
    } catch (err) {
      console.error('Failed to resume chat:', err)
    } finally {
      setIsResuming(false)
    }
  }

  // ─── Save chat settings ───────────────────────────────────────────────────

  // Quick rename from the chat-list "…" menu (targets the menu's chat, not necessarily the open one).
  const saveRename = async () => {
    if (!renameModal) return
    const { chatId: id, name } = renameModal
    const trimmed = name.trim()
    if (!trimmed) { setRenameModal(null); return }
    try {
      const updated = await api.chats.update(id, { name: trimmed } as any)
      setChats(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c))
      if (id === chatId) setCurrentChat(prev => (prev ? { ...prev, ...updated } : prev))
    } catch (err) {
      console.error('Failed to rename chat:', err)
    }
    setRenameModal(null)
  }

  const saveSettings = async () => {
    if (!chatId) return
    try {
      const updated = await api.chats.update(chatId, {
        name:           editName,
        command:        editCommand,
        agentPlugin:    editPlugin || null,
      } as any)
      setCurrentChat(updated)
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, ...updated } : c))
      setShowSettings(false)
    } catch (err) {
      console.error('Failed to save settings:', err)
    }
  }

  const stateColor = (state: string) => state === 'active' ? 'var(--success)' : 'var(--warning)'

  // pane.state badge (spec/CHAT_API.md §7): "AI กำลังตอบ / รอคำตอบ / ตอบจบแล้ว" per chat. Live
  // value from the global pane_state WS, seeded by the REST list field on load. null → no badge.
  const paneBadge = (chat: Chat): { label: string; color: string } | null => {
    const s = wsHook.paneStates[chat.id]?.state ?? chat.paneState?.state
    switch (s) {
      case 'running': return { label: 'กำลังตอบ',  color: 'var(--success)' }
      case 'waiting': return { label: 'รอคำตอบ',   color: 'var(--warning)' }
      case 'idle':    return { label: 'ตอบจบแล้ว', color: 'var(--text-muted)' }
      case 'error':   return { label: 'ผิดพลาด',   color: 'var(--danger)' }
      default:        return null
    }
  }

  // ─── Filtered list (search across everything) ───────────────────────────────

  const matchesSearch = useCallback((c: Chat) => {
    const q = chatSearch.trim().toLowerCase()
    if (!q) return true
    return (
      c.name.toLowerCase().includes(q) ||
      (c.agent_display_name?.toLowerCase().includes(q) ?? false) ||
      (c.workspace_name?.toLowerCase().includes(q) ?? false)
    )
  }, [chatSearch])

  // Flat list: pinned chats first, then by last_activity DESC. Also filtered by origin
  // (spec/CHAT_API.md §6): 'all' | 'user' (UI-created) | 'api' (provisioned via the API).
  const recentChats = useMemo(() => {
    return [...chats]
      .filter(matchesSearch)
      .filter(c => originFilter === 'all' || (c.created_by ?? 'user') === originFilter)
      .sort((a, b) =>
        ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) ||
        ((b.last_activity ?? 0) - (a.last_activity ?? 0)),
      )
  }, [chats, matchesSearch, originFilter])

  // ─── Sidebar chat row (reused in both tabs) ─────────────────────────────────

  const ChatRow = (chat: Chat) => (
    <div
      key={chat.id}
      className={`sidebar-chat-item${chat.id === chatId ? ' active' : ''}`}
      onClick={() => navigate(`/c/${chat.id}`)}
    >
      <span className="sidebar-chat-dot" style={{ color: stateColor(chat.state) }}>●</span>
      <div className="sidebar-chat-text">
        <span className="sidebar-chat-name">
          {!!chat.pinned && <Icon name="pin" size={11} className="sidebar-chat-pin" />}
          {chat.name}
        </span>
        <span className="sidebar-chat-meta">
          {(() => {
            const tag = agentTag(chat, plugins)
            return (
              <span
                className="sidebar-chat-tag"
                style={{ color: tag.color, background: `color-mix(in srgb, ${tag.color} 16%, transparent)` }}
              >
                {tag.label}
              </span>
            )
          })()}
          {(() => {
            const b = paneBadge(chat)
            return b ? (
              <span
                className="sidebar-chat-state"
                style={{ color: b.color, background: `color-mix(in srgb, ${b.color} 16%, transparent)` }}
              >
                {b.label}
              </span>
            ) : null
          })()}
          <RelativeTime ts={chat.last_activity} className="sidebar-chat-time" />
        </span>
      </div>
      <button
        className="sidebar-chat-menu-btn"
        onClick={e => openRowMenu(chat.id, e)}
        title="Options"
        aria-label="Chat options"
      >
        <Icon name="more" size={18} />
      </button>
    </div>
  )

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={`app-shell${sidebarCollapsed ? ' app-shell--collapsed' : ''}`}>

      {/* Mobile sidebar overlay */}
      {showMobileSidebar && (
        <div
          className="mobile-sidebar-overlay"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar${showMobileSidebar ? ' sidebar--open' : ''}`}>
        <div className="sidebar-top">
          <img src="/whimer-wordmark.svg" alt="Whimer" className="app-logo-img" />
          <button
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name="chevron-left" size={18} />
          </button>
        </div>

        <button onClick={() => setShowNewChat(true)} className="sidebar-new-chat">
          <span className="sidebar-new-chat-plus"><Icon name="plus" size={16} strokeWidth={2.5} /></span> New Chat
        </button>

        <div className="sidebar-search-wrap">
          <input
            className="sidebar-search"
            placeholder="Search chats..."
            value={chatSearch}
            onChange={e => setChatSearch(e.target.value)}
          />
          <div className="sidebar-bulk">
            <button
              className="sidebar-bulk-btn"
              type="button"
              onClick={cleanAll}
              title="หยุด AI ทั้งหมด (ประหยัด memory, ประวัติยังอยู่)"
              aria-label="Clean all chats"
            >
              <Icon name="sweep" size={15} />
            </button>
            <button
              className="sidebar-bulk-btn"
              type="button"
              onClick={() => setBulkMenuOpen(o => !o)}
              title="ลบแชท"
              aria-label="Delete chats"
            >
              <Icon name="trash" size={15} />
            </button>
            {bulkMenuOpen && (
              <>
                <div className="sidebar-bulk-backdrop" onClick={() => setBulkMenuOpen(false)} />
                <div className="sidebar-bulk-menu" role="menu">
                  <button className="row-menu-item row-menu-item--danger" type="button" onClick={() => bulkDelete('all')}>
                    <Icon name="trash" size={14} /> ลบทั้งหมด
                  </button>
                  <button className="row-menu-item" type="button" onClick={() => bulkDelete('pinned')}>
                    <Icon name="pin" size={14} /> ลบเฉพาะ Pin
                  </button>
                  <button className="row-menu-item" type="button" onClick={() => bulkDelete('unpinned')}>
                    <Icon name="trash" size={14} /> ลบอันที่ไม่ Pin
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Origin filter (spec/CHAT_API.md §6) — only shown once the API has created a chat. */}
        {chats.some(c => c.created_by === 'api') && (
          <div className="sidebar-origin-filter">
            {([['all', 'ทั้งหมด'], ['user', 'ฉันสร้าง'], ['api', 'API']] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={`sidebar-origin-btn${originFilter === v ? ' active' : ''}`}
                onClick={() => setOriginFilter(v)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="sidebar-chats">
          {chats.length === 0 && (
            <div className="sidebar-empty">No chats yet. Start one with "+ New Chat".</div>
          )}
          {chats.length > 0 && recentChats.length === 0 && chatSearch && (
            <div className="sidebar-empty">No chats match "{chatSearch}"</div>
          )}
          {recentChats.map(ChatRow)}
        </div>

        <div className="sidebar-footer">
          <button
            onClick={() => navigate('/settings')}
            className="sidebar-footer-btn"
          >
            <span className="sidebar-footer-icon"><Icon name="settings" size={16} /></span> Settings
          </button>
        </div>
      </aside>

      {/* Main area */}
      <main className="chat-main">
        {chatId && currentChat ? (
          <>
            <div className="chat-header">
              {/* Mobile: hamburger */}
              <button
                className="btn-icon mobile-menu-btn"
                onClick={() => setShowMobileSidebar(prev => !prev)}
                title="Menu"
                aria-label="Open menu"
              >
                <Icon name="menu" size={20} />
              </button>

              {sidebarCollapsed && (
                <button
                  className="btn-icon sidebar-expand-btn"
                  onClick={() => setSidebarCollapsed(false)}
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                >
                  <Icon name="chevron-right" size={18} />
                </button>
              )}

              <span
                className="chat-header-dot"
                style={{ color: stateColor(currentChat.state) }}
                title={currentChat.state}
              >●</span>
              {(() => {
                const tag = agentTag(currentChat, plugins)
                return (
                  <span
                    className="chat-header-tag"
                    style={{ color: tag.color, background: `color-mix(in srgb, ${tag.color} 16%, transparent)` }}
                  >
                    {tag.label}
                  </span>
                )
              })()}
              <span className="chat-header-name">
                {currentChat.agent_display_name ?? currentChat.name}
              </span>

              <div className="chat-header-spacer" />
              {(() => {
                const caps = capsFor(currentChat, plugins)
                const dualView = caps.cleanView && caps.interactive
                // The mobile "Split" entry: on a narrow screen, when a panel is open, the panel is
                // a full-screen swap → give the view-toggle a 3rd entry to switch chat ⇄ panel.
                // Desktop never needs it (the panel is a side column, both visible at once).
                const showSplit = narrow && activePanel != null
                if (!dualView && !showSplit) return null
                const ev = effectiveView(currentChat, plugins)
                const inPanel = showSplit && mobilePanelView
                // คอม: icon + อักษร · มือถือ (narrow): icon อย่างเดียว (กันปุ่มเบียด header)
                const iconOnly = narrow ? ' view-toggle-btn--icon-only' : ''
                // v2-only chat views: WhimerClean² (clean chat + live widgets) and WhimerTerm²
                // (native terminal). Tapping either also returns from the panel on mobile. Legacy
                // Clean / Terminal live in the settings drawer. See setViewMode.
                return (
                  <div className="view-toggle" role="group" aria-label="View mode">
                    {dualView && (
                      <>
                        {/* v3 views (code-audit §5.1): committed=JSONL only, no oracle → ตารางไม่ฉีก.
                            clean3 = chat skin (default), term3 = terminal skin — same WhimerV3View core.
                            (legacy Clean²/Term²/Clean/WhimerTerm ถอดออกแล้ว 2026-06-11) */}
                        <button
                          type="button"
                          className={`view-toggle-btn${iconOnly}${!inPanel && ev === 'clean3' ? ' view-toggle-btn--active' : ''}`}
                          onClick={() => { setMobilePanelView(false); setViewMode('clean3') }}
                          title="Clean³ — บทสนทนา clean (committed=JSONL)"
                          aria-label="Clean³"
                        ><Icon name="spark" size={14} />{!narrow && <span>Clean³</span>}</button>
                        <button
                          type="button"
                          className={`view-toggle-btn${iconOnly}${!inPanel && ev === 'term3' ? ' view-toggle-btn--active' : ''}`}
                          onClick={() => { setMobilePanelView(false); setViewMode('term3') }}
                          title="Term³ — terminal skin (committed=JSONL)"
                          aria-label="Term³"
                        ><Icon name="terminal" size={14} />{!narrow && <span>Term³</span>}</button>
                      </>
                    )}
                    {!dualView && showSplit && (
                      // terminal-only chat with a panel open: a plain "Chat" entry to return.
                      <button
                        type="button"
                        className={`view-toggle-btn${iconOnly}${!inPanel ? ' view-toggle-btn--active' : ''}`}
                        onClick={() => setMobilePanelView(false)}
                        title="Chat"
                        aria-label="Chat"
                      ><Icon name="terminal" size={14} />{!narrow && <span>Chat</span>}</button>
                    )}
                    {showSplit && (
                      // Neutral "Split" entry — generic icon + label, not tied to the active panel
                      // (only one panel today; a plugin override can come later). See PLUGIN_MODULES.md.
                      <button
                        type="button"
                        className={`view-toggle-btn${iconOnly}${inPanel ? ' view-toggle-btn--active' : ''}`}
                        onClick={() => setMobilePanelView(true)}
                        title="Split"
                        aria-label="Split"
                      ><Icon name="split" size={14} />{!narrow && <span>Split</span>}</button>
                    )}
                  </div>
                )
              })()}
              {hasSidebarSlots && (
                <button
                  onClick={() => openPanel('canvas')}
                  className={`btn-icon chat-settings-btn${activePanel === 'canvas' ? ' chat-settings-btn--active' : ''}`}
                  title="Canvas"
                  aria-label="Toggle canvas"
                >
                  <Icon name="pencil" size={17} />
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                className="btn-icon chat-settings-btn"
                title="Chat settings"
                aria-label="Chat settings"
              >
                <Icon name="settings" size={17} />
              </button>
            </div>

            <div className="chat-body">
              <div
                className="chat-content"
                // Mobile: when viewing a panel full-screen, hide the chat (the panel takes over).
                // Desktop: always visible (the panel is a side column). Kept mounted either way.
                style={narrow && activePanel && mobilePanelView ? { display: 'none' } : undefined}
              >
                {currentChat.state === 'sleeping' ? (
                  <div className="terminal-sleeping">
                    {isResuming ? (
                      <>
                        <span>Starting chat</span>
                        <span className="thinking-dots">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Chat stopped</span>
                        <button onClick={resumeChat} className="btn-primary">Resume</button>
                      </>
                    )}
                  </div>
                ) : effectiveView(currentChat, plugins) === 'clean3' ? (
                  <WhimerV3View
                    key={chatId}
                    skin="clean"
                    chatId={chatId}
                    chat={currentChat}
                    wsHook={wsHook}
                    liveDisabled={capsFor(currentChat, plugins).liveView === false}
                    refreshNonce={refreshNonce}
                    onActivity={() => bumpChatActivity(chatId)}
                    onToggleVoice={voiceEnabled ? () => openPanel('voice') : undefined}
                    voiceActive={activePanel === 'voice'}
                  />
                ) : (
                  <WhimerV3View
                    key={chatId}
                    skin="term"
                    chatId={chatId}
                    chat={currentChat}
                    wsHook={wsHook}
                    liveDisabled={capsFor(currentChat, plugins).liveView === false}
                    refreshNonce={refreshNonce}
                    onActivity={() => bumpChatActivity(chatId)}
                    onToggleVoice={voiceEnabled ? () => openPanel('voice') : undefined}
                    voiceActive={activePanel === 'voice'}
                  />
                )}
              </div>

              {/* Panel host — one active panel (Voice first). Desktop: a resizable side column
                  (drag the handle on its left edge). Mobile: full-screen swap, shown when
                  mobilePanelView (kept mounted while hidden so the panel's connection survives
                  switching back to chat). See PLUGIN_MODULES.md. */}
              {activePanel && (activePanel !== 'voice' || voiceEnabled) && (
                <>
                  {!narrow && (
                    <div
                      className="panel-resize-handle"
                      onPointerDown={startPanelResize}
                      title="Drag to resize"
                      role="separator"
                      aria-orientation="vertical"
                    />
                  )}
                  <div
                    className="panel-host-col"
                    style={narrow ? { display: mobilePanelView ? 'flex' : 'none' } : { width: panelWidth }}
                  >
                    <PanelHost
                      key={`${activePanel}:${chatId}`}
                      activePanelId={activePanel}
                      chatId={chatId}
                      workspaceId={currentChat.workspace_id}
                      onClose={closePanel}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="chat-empty">
            {/* Mobile: hamburger on empty state */}
            <button
              className="btn-icon mobile-menu-btn mobile-menu-btn--floating"
              onClick={() => setShowMobileSidebar(prev => !prev)}
              title="Menu"
              aria-label="Open menu"
            >
              <Icon name="menu" size={20} />
            </button>
            {sidebarCollapsed && (
              <button
                className="btn-icon sidebar-expand-btn sidebar-expand-btn--floating"
                onClick={() => setSidebarCollapsed(false)}
                title="Expand sidebar"
                aria-label="Expand sidebar"
              >
                <Icon name="chevron-right" size={18} />
              </button>
            )}
            <div className="chat-empty-inner">
              <span className="chat-empty-icon">&#x2B21;</span>
              <h2 className="chat-empty-title">What can I help with?</h2>
              <p className="chat-empty-sub">Start a new conversation with an agent on your server.</p>
              <button onClick={() => setShowNewChat(true)} className="btn-primary btn-lg">
                + New Chat
              </button>
            </div>
          </div>
        )}
      </main>

      {/* New Chat Modal */}
      {showNewChat && (
        <NewChatModal
          workspaceId={currentChat?.workspace_id ?? ''}
          plugins={plugins}
          onClose={() => setShowNewChat(false)}
          onSubmit={handleCreateChat}
          onOpenExistingChat={handleOpenExistingChat}
        />
      )}

      {/* Chat Settings panel */}
      {showSettings && currentChat && (
        <div className="drawer-overlay" onClick={() => setShowSettings(false)}>
          <div
            className="settings-drawer"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-label="Chat settings"
          >
            <div className="settings-drawer-head">
              <h3>Chat Settings</h3>
              <button
                className="btn-icon"
                onClick={() => setShowSettings(false)}
                aria-label="Close settings"
              >
                <Icon name="x" size={18} />
              </button>
            </div>

            <div className="settings-drawer-body">
            <label className="modal-label">
              Name
              <input
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="modal-input"
              />
            </label>

            <label className="modal-label">
              Open in terminal (same live session)
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="modal-input"
                  style={{ flex: 1, fontFamily: 'var(--font-mono, monospace)' }}
                  value={`tmux attach -t whimer-${currentChat.id}`}
                  readOnly
                  onFocus={e => e.currentTarget.select()}
                />
                <button
                  type="button"
                  className="btn-primary"
                  onClick={async () => {
                    const ok = await copyText(`tmux attach -t whimer-${currentChat.id}`)
                    if (ok) {
                      setCopiedTmux(true)
                      setTimeout(() => setCopiedTmux(false), 1500)
                    } else {
                      // insecure context + execCommand both failed — tell the user
                      // to copy manually from the (already selectable) field
                      setCopiedTmux(false)
                      ;(document.activeElement as HTMLElement)?.blur?.()
                      alert('คัดลอกอัตโนมัติไม่ได้ (เบราว์เซอร์บล็อกบน HTTP) — แตะที่ช่องคำสั่งแล้วคัดลอกเองได้เลย')
                    }
                  }}
                >
                  {copiedTmux ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <span className="modal-hint">
                รันใน VPS terminal เพื่อเข้า session เดียวกันแบบ live (อย่าใช้ <code>claude --resume</code>)
              </span>
            </label>

            <label className="modal-label">
              Agent used
              <input
                className="modal-input"
                value={currentChat.agent_display_name || currentChat.agent_plugin || 'none'}
                disabled
                readOnly
              />
            </label>

              <div className="settings-drawer-section">
                <span className="modal-label" style={{ marginBottom: 8 }}>ข้อมูลแชท</span>
                <button
                  onClick={() => { setRefreshNonce(n => n + 1); setShowSettings(false) }}
                  className="settings-action-btn"
                >
                  <Icon name="refresh" size={16} /> ซิงค์ใหม่จาก JSONL
                </button>
                <span className="modal-hint">
                  ดึงประวัติจาก JSONL ของ agent มาเรียง/เติมใหม่ ใช้เมื่อข้อความเรียงเพี้ยนหรือหาย — ไม่ต้องรีโหลดทั้งหน้า (ไม่แตะ process ที่รันอยู่)
                </span>
                <button
                  onClick={async () => {
                    try { await api.chats.clearPending(currentChat.id) } catch (e) { console.error('[clear-pending]', e) }
                    setRefreshNonce(n => n + 1); setShowSettings(false)
                  }}
                  className="settings-action-btn"
                  style={{ marginTop: 10 }}
                >
                  <Icon name="trash" size={16} /> เคลียร์ข้อความค้าง “กำลังส่ง”
                </button>
                <span className="modal-hint">
                  ลบสถานะค้างของข้อความที่ส่ง + AI ตอบไปแล้ว (เจอ “กำลังส่ง/รอคิว” ค้างทั้งที่จบไปแล้ว) — ลบเฉพาะที่ยืนยันใน JSONL แล้ว (FIFO ไม่แตะที่ยังรอจริง)
                </span>
              </div>
            </div>

            <div className="settings-drawer-foot">
              <button onClick={() => setShowSettings(false)} className="btn-ghost">Cancel</button>
              <button onClick={saveSettings} className="btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-row "..." menu (fixed-position so it escapes the sidebar overflow) */}
      {menuOpenId && (() => {
        const mc = chats.find(c => c.id === menuOpenId)
        if (!mc) return null
        return (
          <div
            className="row-menu"
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
            onClick={e => e.stopPropagation()}
          >
            <button className="row-menu-item" onClick={e => togglePin(mc, e)}>
              <Icon name="pin" size={15} /> {mc.pinned ? 'Unpin' : 'Pin to top'}
            </button>
            <button
              className="row-menu-item"
              onClick={e => { e.stopPropagation(); setMenuOpenId(null); setRenameModal({ chatId: mc.id, name: mc.name ?? '' }) }}
            >
              <Icon name="pencil" size={15} /> Rename
            </button>
            <button
              className="row-menu-item"
              onClick={e => { e.stopPropagation(); setMenuOpenId(null); setTokenModal({ chatId: mc.id, token: mc.api_token ?? null }) }}
            >
              <Icon name="terminal" size={15} /> API token
            </button>
            <button className="row-menu-item row-menu-item--danger" onClick={e => deleteChat(mc.id, e)}>
              <Icon name="trash" size={15} /> Delete
            </button>
          </div>
        )
      })()}

      {/* Quick rename modal (chat-list "…" menu) */}
      {renameModal && (
        <div className="modal-overlay" onClick={() => setRenameModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3>Rename chat</h3>
            <label className="modal-label">
              Name
              <input
                className="modal-input"
                autoFocus
                value={renameModal.name}
                onChange={e => setRenameModal(r => (r ? { ...r, name: e.target.value } : r))}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveRename()
                  else if (e.key === 'Escape') setRenameModal(null)
                }}
              />
            </label>
            <div className="modal-actions">
              <button className="btn-ghost" type="button" onClick={() => setRenameModal(null)}>Cancel</button>
              <button className="btn-primary" type="button" onClick={saveRename}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-chat API token modal (spec/CHAT_API.md §5) */}
      {tokenModal && (() => {
        const mc = chats.find(c => c.id === tokenModal.chatId)
        return (
          <div className="modal-overlay" onClick={() => setTokenModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <h3 style={{ margin: '0 0 6px' }}>API token{mc ? ` — ${mc.name}` : ''}</h3>
              <p className="settings-hint">
                ใช้เป็น header <code>X-Chat-Token</code> เรียก <code>/api/v1/chats/{tokenModal.chatId}/*</code> (ส่ง / อ่าน / สถานะ / ตอบ / ลบ เฉพาะแชทนี้). เก็บเป็นความลับ.
              </p>
              {tokenModal.token ? (
                <code style={{ display: 'block', wordBreak: 'break-all', padding: '8px 10px', background: 'var(--bg-elevated, rgba(127,127,127,0.1))', borderRadius: 6, fontSize: 12, margin: '8px 0' }}>
                  {tokenModal.token}
                </code>
              ) : (
                <p className="settings-hint" style={{ margin: '8px 0' }}>แชทนี้ยังไม่มี token</p>
              )}
              <div className="modal-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {tokenModal.token && (
                  <button className="settings-action-btn" onClick={() => navigator.clipboard?.writeText(tokenModal.token!)}>คัดลอก</button>
                )}
                <button className="settings-action-btn" disabled={tokenBusy} onClick={() => genChatToken(tokenModal.chatId)}>
                  {tokenModal.token ? 'สร้างใหม่' : 'สร้าง token'}
                </button>
                <button className="settings-action-btn" onClick={() => setTokenModal(null)}>ปิด</button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
